<template>
  <div class="app-body research_group">
    <!-- render profile header -->
    <research-group-header :researchGroup="researchGroup" :isLoaded="isLoaded" :hasResearch="hasResearch" />

    <!-- render static messages -->
    <div class="app-body_show container-fluid mt-5 mb-0" v-if="error || success">
      <static-messages v-if="error" :messages="error" :variant="`danger`" :containerClass="`mb-0`" @closed="error = null"/>
      <static-messages v-if="success" :messages="success" :variant="`success`" :containerClass="`mb-0`" @closed="success = null"/>
    </div>

    <!-- render template -->
    <router-view
      :researchGroup="researchGroup"
      :searchFilterObject="searchFilterObject"
      :isLoaded="isLoaded"
      :hasResearch="hasResearch"
      :indexes="indexes"
    />
  </div>
</template>

<script>
import highlights from '@/mixins/highlights'

import ResearchGroupService from '@/services/research_group'
import SearchService from '@/services/search'

import ResearchGroupHeader from '@/components/research_group/Header'
import StaticMessages from '@/components/shared/StaticMessages'
import MPService from '@/services/mixpanel'

export default {
  name: 'researchGroupBase',
  mixins: [highlights],
  components: {
    ResearchGroupHeader,
    StaticMessages
  },
  data () {
    return {
      success: null,
      error: null,
      isLoaded: false,
      researchGroup: {},
      hasResearch: false,
      indexes: {},
      objectForHighlighting: {
        properties: ['name', 'description', 'about'],
        arrays: ['organisations.name'],
        owners: ['researchers.initial_name.normalized', 'researchers.full_name.normalized']
      },
      suggestionIndexes: ['projects', 'publications', 'clinical_trials', 'patents']
    }
  },
  computed: {
    searchFilterObject () {
      return {
        key: 'research_groups',
        values: '',
        index: 'researchers',
        global: false,
        suggestions: []
      }
    }
  },
  beforeRouteUpdate (to, from, next) {
    if (to.params.id !== from.params.id) this.fetchResearchGroup(to.params.id)
    this.checkOpenModal(to.query)
    next()
  },
  created () {
    this.fetchResearchGroup(this.$route.params.id)
  },
  mounted () {
    // invites are triggered
    this.$events.$on('research_group:community:invited', (resp) => {
      this.success = `Research group is successfully invited`
    })

    // Update all values of current RG with new RG values
    this.$events.$on('research_group:updated:all', (researchGroup) => {
      this.researchGroup = {
        ...this.researchGroup,
        ...researchGroup
      }
      this.fetchHighlights()
    })

    // research group base call
    this.$events.$on('research_group:updated', (key, value) => {
      if (this.researchGroup.hasOwnProperty(key)) {
        this.$set(this.researchGroup, key, value)
        this.fetchHighlights()
      }
    })

    // service of research topic is removed
    this.$events.$on('research_group:removed', (key, value) => {
      if (this.researchGroup.hasOwnProperty(key)) {
        this.researchGroup[key].splice(this.researchGroup[key].findIndex((i) => this.hasMatchingSlug(i.slug_values, value)), 1)
      }
    })

    this.$events.$on('bookmark_folder:bookmarked', (bookmarkFolders, index) => {
      if (index === 'research_groups') this.$set(this.researchGroup, 'bookmark_folders', bookmarkFolders)
    })

    this.$events.$on('pusher:community-research_group-invited', () => this.researchGroup.pending_invites_count++)
    this.$events.$on('pusher:community-research_group-cancelled', () => this.researchGroup.pending_invites_count--)
    this.$events.$on('pusher:research_group-user-invite_request', () => this.researchGroup.pending_invites_count++)
    this.$events.$on('pusher:research_group-user-invite_request-cancelled', () => {
      this.$set(this.researchGroup, 'user_invite_action', 'cancelled')
      this.$set(this.researchGroup, 'users', this.researchGroup.pending_invites_count--)
    })
    this.$events.$on('pusher:user-invite_request-declined', () => this.$set(this.researchGroup, 'user_invite_action', 'declined'))

    this.$events.$on('pusher:user-invite_request-confirmed', (data) => {
      this.$set(this.researchGroup, 'user_invite_action', 'confirmed')
      this.$set(this.researchGroup, 'rw_access', data.rw_access)
      this.$set(this.researchGroup, 'users', this.researchGroup.users++)
    })

    this.checkOpenModal(this.$route.query)
  },
  metaInfo () {
    return {
      titleTemplate: this.isLoaded ? `${this.researchGroup.name} | AcademicLabs` : `%s`,
      meta: [
        { vmid: 'description', name: 'description', content: this.isLoaded ? this.stripTags(this.researchGroup.description) : '' }
      ]
    }
  },
  methods: {
    fetchResearchGroup (id) {
      this.isLoaded = false

      ResearchGroupService.get(id).then(researchGroup => {
        this.researchGroup = researchGroup
        this.fetchHighlights()
        this.setSuggestions(this.researchGroup.slug_values, this.researchGroup.researchers || [])
          .then(this.fetchResearchCounts)

        if (this.$route.query.success) {
          this.success = `Successfully accepted invite to join ${this.researchGroup.name}.`
          this.$router.replace({ name: 'research_group.show', params: this.$route.params })
        }

        MPService.trackResearchGroupVisit()
      }).catch(err => {
        this.error = this.handleError(err)
        this.isLoaded = !this.isEmpty(this.error)
      })
    },
    fetchHighlights () {
      SearchService.highlightsDetailView(this.getSearchQueryForHighlighting('research_groups', this.researchGroup.slug_values)).then(highlights => {
        let parsedHighlights = this.generateHighlights(this.researchGroup, highlights, this.objectForHighlighting)
        for (let esProperty of this.objectForHighlighting.owners) this.setForArray('owners', esProperty.split('.'), esProperty, true)
        this.$set(this.researchGroup, 'highlights', parsedHighlights)
        this.isLoaded = true
      }).catch(err => {
        this.error = this.handleError(err)
      })
    },
    setSuggestions (slugValues, suggestions) {
      this.searchFilterObject.suggestions = []
      this.searchFilterObject.suggestion_indexes = this.suggestionIndexes
      this.searchFilterObject.values = slugValues
      this.searchFilterObject.label = this.researchGroup.name
      for (let suggestion of suggestions) {
        this.searchFilterObject.suggestions.push({
          type: 'person',
          must: this.getFullName(suggestion),
          must_not: suggestion.slug_values
        })
      }
      this.searchFilterObject.filter_order = [this.searchFilterObject.key]
      this.searchFilterObject.filters = {}
      this.searchFilterObject.filters[this.searchFilterObject.key] = {}
      this.searchFilterObject.filters[this.searchFilterObject.key].buckets = []
      this.searchFilterObject.filters[this.searchFilterObject.key].buckets.push({ active: true, values: slugValues })

      return Promise.resolve()
    },
    fetchResearchCounts () {
      this.indexes = {}
      // Fetch counts for research of a RG to determine the default index we want to redirect to on "Research" click
      SearchService.counts(this.searchFilterObject).then(counts => {
        if (!counts || !counts.categories) return
        this.indexes = counts.categories
        const [index, indexData] = this.getMostRelevantIndex(counts.categories, 'researchers')
        this.searchFilterObject.index = index
        this.hasResearch = indexData.total > 0
      }).catch(() => {
        // It's fiiine
      })
    }
  },
  destroyed () {
    this.$events.$off('bookmark_folder:bookmarked')
    this.$events.$off('research_group:removed')
    this.$events.$off('research_group:updated')
    this.$events.$off('research_group:updated:all')
    this.$events.$off('research_group:community:invited')
    this.$events.$off('pusher:community-research_group-invited')
    this.$events.$off('pusher:community-research_group-cancelled')
    this.$events.$off('pusher:research_group-user-invite_request')
    this.$events.$off('pusher:research_group-user-invite_request-cancelled')
    this.$events.$off('pusher:user-invite_request-confirmed')
    this.$events.$off('pusher:user-invite_request-declined')
  }
}
</script>
